import {
  Box,
  VStack,
} from "@chakra-ui/react";
import React, { useState, useEffect, useMemo, useRef } from "react";
import DataScopeConfigurator from "./DataScopeConfigurator";
import { AnalystProto, ColumnScopeProto, DataScopeProto } from "build-ts/analyst";
import { Header, MetricProto } from "build-ts/metadata";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { getAllMetrics } from "services/MetricService";
import showToast from "hooks/useCustomToast";

interface AnalystDataAccessComponentProps {
  analyst: AnalystProto | undefined;
  setAnalyst: React.Dispatch<React.SetStateAction<AnalystProto | undefined>>;
  isCreatingNew?: boolean;
  hasDiscardedChanges?: boolean;
  setHasDiscardedChanges?: React.Dispatch<React.SetStateAction<boolean>>;
  originalAnalyst?: AnalystProto;
}

const AnalystDataAccessComponent: React.FC<AnalystDataAccessComponentProps> = ({
  analyst,
  setAnalyst,
  isCreatingNew = false,
  hasDiscardedChanges = false,
  setHasDiscardedChanges,
  originalAnalyst
}) => {
  // Data Scope individual pieces
  const [dsInstructions, setDsInstructions] = useState<string>("");
  const [dsUseDefaultScope, setDsUseDefaultScope] = useState<boolean>(true);
  const [selectedMetricsWithEdges, setSelectedMetricsWithEdges] = useState<MetricProto[]>([]);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<Header[]>([]);
  const [columnScopes, setColumnScopes] = useState<ColumnScopeProto[]>([]);

  // Track the last loaded analyst
  const lastLoadedAnalystIdRef = useRef<string | undefined>(undefined);
  // Track if we're doing initial sync
  const isInitialSyncRef = useRef<boolean>(true);


  // Sync local state with analyst data scope
  useEffect(() => {
    const loadDataScope = async () => {
      const currentAnalystId = analyst?.header?.id;

      // Set flag that we're doing initial sync
      isInitialSyncRef.current = true;

      // Only sync if:
      // 1. We're creating a new analyst (isCreatingNew), OR
      // 2. The analyst ID has actually changed (loading a different analyst)
      if (isCreatingNew) {
        // Reset form for new analyst
        lastLoadedAnalystIdRef.current = undefined;
        setDsInstructions("");
        setDsUseDefaultScope(true);
        setSelectedMetricsWithEdges([]);
        setSelectedBenchmarks([]);
        setColumnScopes([]);
        isInitialSyncRef.current = false;
        return;
      }

      // Check if this is a different analyst than the one we last loaded
      if ((currentAnalystId && currentAnalystId !== lastLoadedAnalystIdRef.current && !hasDiscardedChanges)) {
        lastLoadedAnalystIdRef.current = currentAnalystId;

        // Initialize data scope
        if (analyst?.data_scope) {
          setDsInstructions(analyst.data_scope.instructions || "");
          setDsUseDefaultScope(analyst.data_scope.use_default_scope || false);

          // Get metrics from data scope
          const metricsFromDataScope = analyst.data_scope.metrics || [];
          if (metricsFromDataScope.length > 0) {
            try {
              const metricIds = metricsFromDataScope
                .map(m => m.header?.id)
                .filter((id): id is string => !!id);

              if (metricIds.length > 0) {
                const metricsWithEdges = await getAllMetrics(metricIds, true);
                setSelectedMetricsWithEdges(metricsWithEdges || []);
              }
            } catch (error) {
              console.error("Error fetching metrics with edges:", error);
              showToast("Failed to load metric edges", "", "error");
              setSelectedMetricsWithEdges(metricsFromDataScope); // Fallback without edges
            }
          } else {
            setSelectedMetricsWithEdges([]);
          }

          // Extract benchmarks
          const benchmarkHeaders = analyst.data_scope.benchmarks
            ?.map(b => b.header)
            .filter((h): h is Header => h !== undefined) || [];
          setSelectedBenchmarks(benchmarkHeaders);

          // Column scopes
          setColumnScopes(analyst.data_scope.column_scopes || []);
        } else {
          // Reset to defaults
          setDsInstructions("");
          setDsUseDefaultScope(true);
          setSelectedMetricsWithEdges([]);
          setSelectedBenchmarks([]);
          setColumnScopes([]);
        }
      }
      // Allow parent updates only after initial sync is complete
      isInitialSyncRef.current = false;
    };

    loadDataScope();
  }, [analyst?.header?.id, isCreatingNew]);

  // Load original data when discarded changes are detected
  useEffect(() => {
    const loadOriginalData = async () => {
      if (originalAnalyst && hasDiscardedChanges) {
        // Initialize data scope from original
        if (originalAnalyst?.data_scope) {
          setDsInstructions(originalAnalyst.data_scope.instructions || "");
          setDsUseDefaultScope(originalAnalyst.data_scope.use_default_scope || false);

          // Get metrics from data scope
          const metricsFromDataScope = originalAnalyst.data_scope.metrics || [];
          if (metricsFromDataScope.length > 0) {
            try {
              const metricIds = metricsFromDataScope
                .map(m => m.header?.id)
                .filter((id): id is string => !!id);

              if (metricIds.length > 0) {
                const metricsWithEdges = await getAllMetrics(metricIds, true);
                setSelectedMetricsWithEdges(metricsWithEdges || []);
              }
            } catch (error) {
              console.error("Error fetching metrics with edges:", error);
              showToast("Failed to load metric edges", "", "error");
              setSelectedMetricsWithEdges(metricsFromDataScope);
            }
          } else {
            setSelectedMetricsWithEdges([]);
          }

          // Extract benchmarks
          const benchmarkHeaders = originalAnalyst.data_scope.benchmarks
            ?.map(b => b.header)
            .filter((h): h is Header => h !== undefined) || [];
          setSelectedBenchmarks(benchmarkHeaders);

          // Column scopes
          setColumnScopes(originalAnalyst.data_scope.column_scopes || []);
        } else {
          // Reset to defaults
          setDsInstructions("");
          setDsUseDefaultScope(true);
          setSelectedMetricsWithEdges([]);
          setSelectedBenchmarks([]);
          setColumnScopes([]);
        }
        setHasDiscardedChanges?.(false);
      }
    };
    loadOriginalData();
  }, [hasDiscardedChanges]);

  // Build DataScopeProto from individual pieces using useMemo
  const dataScope = useMemo((): DataScopeProto | undefined => {
    if (dsUseDefaultScope) {
      return DataScopeProto.create({
        instructions: dsInstructions || undefined,
        use_default_scope: true,
        metrics: [],
        benchmarks: [],
        column_scopes: [],
      });
    }

    if (selectedMetricsWithEdges.length === 0) {
      return undefined;
    }

    // Convert selectedBenchmarks to BenchmarkEntityProto
    const benchmarks = selectedBenchmarks.map(b =>
      BenchmarkEntityProto.create({
        header: b,
        benchmark_type: b.benchmark_type,
      })
    );

    return DataScopeProto.create({
      instructions: dsInstructions || undefined,
      use_default_scope: false,
      metrics: selectedMetricsWithEdges,
      benchmarks,
      column_scopes: columnScopes,
    });
  }, [dsInstructions, dsUseDefaultScope, selectedMetricsWithEdges, selectedBenchmarks, columnScopes]);

  // Update parent analyst state whenever data scope changes
  useEffect(() => {
    if (isInitialSyncRef.current) {
      return;
    }

    if (analyst) {
      setAnalyst({
        ...analyst,
        data_scope: dataScope,
      });
    }
  }, [dataScope]);

  return (
    <Box
      position="relative"
      mx="auto"
      height="100%"
      p={4}
      overflowY="auto"
      sx={{
        "&::-webkit-scrollbar": {
          display: "none"
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none"
      }}
    >
      <VStack spacing={3} align="stretch" height="100%">
        <DataScopeConfigurator
          originalAnalyst={originalAnalyst}
          instructions={dsInstructions}
          setInstructions={setDsInstructions}
          useDefaultScope={dsUseDefaultScope}
          setUseDefaultScope={setDsUseDefaultScope}
          selectedMetricsWithEdges={selectedMetricsWithEdges}
          setSelectedMetricsWithEdges={setSelectedMetricsWithEdges}
          selectedBenchmarks={selectedBenchmarks}
          setSelectedBenchmarks={setSelectedBenchmarks}
          columnScopes={columnScopes}
          setColumnScopes={setColumnScopes}
          isCreatingNew={isCreatingNew}
        />
      </VStack>
    </Box>
  );
};

export default AnalystDataAccessComponent;
